import React, { useState, useEffect, Suspense } from 'react';
import Sidebar from './components/Sidebar';
import ContextSelector from './components/ContextSelector';
import { ViewState, ReconciliationSettings, MatchStatus } from './types';
import { useAuthStore } from './stores/useAuthStore';
import { useSettingsStore } from './stores/useSettingsStore';
import { useDataStore } from './stores/useDataStore';
import { LogOut, User as UserIcon, Loader2, Shield } from 'lucide-react';
import ErrorBoundary from './components/ErrorBoundary';
import { deriveKey } from './services/crypto';
import CryptoJS from 'crypto-js';

// Lazy Load Components
const FileUpload = React.lazy(() => import('./components/FileUpload'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const ReconciliationTable = React.lazy(() => import('./components/ReconciliationTable'));
const Settings = React.lazy(() => import('./components/Settings'));
const DataEditor = React.lazy(() => import('./components/DataEditor'));

const LoadingFallback = () => (
  <div className="flex h-full items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    <span className="ml-2 text-slate-500">Loading module...</span>
  </div>
);

const App: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const { settings, loadSettings, saveSettings, encryptionKey, setEncryptionKey } = useSettingsStore();
  const {
    clients, activeClient, activePeriod, syncStatus,
    setClients, setActiveClient, setActivePeriod, addClient, updateClient, setBulkClients,
    initializeStore, loadDataForPeriod, processDataUpdate, updateReconciliationItem, bulkUpdateReconciliationItems,
    backupData, restoreData, syncToDrive, loadFromDrive, syncFromPortal, getCurrentData
  } = useDataStore();

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [currentView, setCurrentView] = useState<ViewState>('upload');
  const [dashboardFilter, setDashboardFilter] = useState<{ status?: MatchStatus | null, remark?: string } | null>(null);
  const [masterPasswordInput, setMasterPasswordInput] = useState('');

  // Mobile & Desktop Sidebar State
  // Default to open on desktop (width >= 768px), closed on mobile
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return false;
  });

  // Initial Load
  useEffect(() => {
    loadSettings().then(() => {
      initializeStore();
    });
  }, [loadSettings, initializeStore]);

  // Load Data when Context Changes
  useEffect(() => {
    if (activeClient && activePeriod) {
      loadDataForPeriod(activeClient.id, activePeriod);
    }
  }, [activeClient, activePeriod, loadDataForPeriod]);

  const currentData = getCurrentData();

  const handleDashboardFilter = (status: MatchStatus, remark?: string) => {
    setDashboardFilter({ status, remark });
    setCurrentView('reconciliation');
  };

  const handleLogout = () => {
    setUser(null);
    setCurrentView('upload');
    setDashboardFilter(null);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const validUser = (settings.users || []).find(u => u.username === loginForm.username && u.password === loginForm.password);

    if (validUser) {
      setUser(validUser);
    } else {
      alert("Invalid username or password.");
    }
  };

  const handleDataImport = (newPr: any[], newGstr: any[]) => {
    const existingPr = currentData?.prData || [];
    const existingGstr = currentData?.gstrData || [];

    const existingPrIds = new Set(existingPr.map(i => i.id));
    const existingGstrIds = new Set(existingGstr.map(i => i.id));

    const finalPr = [...existingPr, ...newPr.filter(i => !existingPrIds.has(i.id))];
    const finalGstr = [...existingGstr, ...newGstr.filter(i => !existingGstrIds.has(i.id))];

    processDataUpdate(finalPr, finalGstr);
    setCurrentView('data-editor');
  };

  const handleUpdateRawData = (type: 'PR' | '2B', updatedList: any[]) => {
    const newPr = type === 'PR' ? updatedList : (currentData?.prData || []);
    const newGstr = type === '2B' ? updatedList : (currentData?.gstrData || []);
    processDataUpdate(newPr, newGstr);
  };

  const handleBulkAddClients = (newClients: any[]) => {
    setBulkClients(newClients);
  };

  const handleMasterPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const key = deriveKey(masterPasswordInput, settings.salt);
    const hash = CryptoJS.SHA256(masterPasswordInput + (settings.salt || '')).toString();
    if (hash === settings.masterPasswordHash) {
      setEncryptionKey(key);
    } else {
      alert("Invalid Master Password.");
    }
  };

  const isFullHeightView = currentView === 'data-editor' || currentView === 'reconciliation' || currentView === 'settings';

  // --- Auth & Security Screens ---
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-blue-600"></div>
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mb-4 transform rotate-12 shadow-xl">
              <span className="text-3xl font-black text-white -rotate-12 italic">R</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Secure Login</h2>
            <p className="text-sm text-slate-500 mt-1 font-medium tracking-wide uppercase">GSTR-2B Reconciler Pro</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Username</label>
              <input type="text" className="w-full border border-slate-200 p-4 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 focus:bg-white transition-all" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })} placeholder="Enter username" />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest pl-1">Password</label>
              <input type="password" className="w-full border border-slate-200 p-4 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 focus:bg-white transition-all" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Enter password" />
            </div>
            <button className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98]">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  if (settings.masterPasswordSet && !encryptionKey) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl border border-slate-800/10">
          <div className="flex flex-col items-center mb-8">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4 text-blue-600 relative">
              <Shield size={40} />
              <div className="absolute inset-0 bg-blue-400 opacity-10 rounded-full animate-ping"></div>
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Verification Required</h2>
            <p className="text-sm text-slate-500 mt-2 text-center max-w-[240px]">Enter your Master Password to decrypt your application data.</p>
          </div>
          <form onSubmit={handleMasterPasswordSubmit} className="space-y-5">
            <input
              type="password"
              autoFocus
              className="w-full border border-slate-200 p-4 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-center text-lg tracking-widest font-mono"
              value={masterPasswordInput}
              onChange={e => setMasterPasswordInput(e.target.value)}
              placeholder="••••••••"
            />
            <button className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold hover:bg-blue-700 transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98]">Unlock Vault</button>
          </form>
          <button
            onClick={handleLogout}
            className="w-full mt-8 text-sm font-bold text-slate-400 hover:text-red-500 transition-colors uppercase tracking-widest"
          >
            Switch User / Logout
          </button>
        </div>
      </div>
    );
  }

  // --- Main Application UI ---
  return (
    <div className="flex h-screen bg-slate-50 font-sans text-slate-900">
      <Sidebar
        currentView={currentView}
        setView={(view) => {
          setCurrentView(view);
          if (window.innerWidth < 768) setIsSidebarOpen(false);
        }}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
        <div className="bg-white border-b border-slate-200 p-3.5 flex justify-between items-center shadow-sm z-20">
          <div className="flex items-center gap-3">
            <button
              className="p-2.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            >
              <div className="space-y-1.5">
                <span className={`block w-5 h-0.5 bg-current transition-transform duration-300 ${isSidebarOpen ? 'translate-y-2 rotate-45' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-current transition-opacity duration-300 ${isSidebarOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-5 h-0.5 bg-current transition-transform duration-300 ${isSidebarOpen ? '-translate-y-2 -rotate-45' : ''}`}></span>
              </div>
            </button>
            <ContextSelector
              clients={clients}
              activeClient={activeClient}
              activePeriod={activePeriod}
              onClientChange={setActiveClient}
              onPeriodChange={setActivePeriod}
              onAddClient={addClient}
              onUpdateClient={updateClient}
              onBulkAddClients={handleBulkAddClients}
            />
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3 border border-slate-100 bg-slate-50/50 px-4 py-2 rounded-xl">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                <UserIcon size={16} />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-bold text-slate-700 leading-tight">{user?.username || 'User'}</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">{user?.role || 'Guest'}</span>
              </div>
            </div>
            <button onClick={handleLogout} className="group text-slate-300 hover:text-red-500 hover:bg-red-50 p-2.5 rounded-xl transition-all" title="Logout">
              <LogOut size={20} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>
        <main className={`flex-1 relative p-3 bg-slate-100 ${isFullHeightView ? 'overflow-hidden' : 'overflow-auto'}`}>
          <div className={`w-full ${isFullHeightView ? 'h-full' : 'min-h-full'} rounded-2xl bg-white shadow-sm border border-slate-200 ${isFullHeightView ? 'overflow-hidden' : ''} relative`}>
            <Suspense fallback={<LoadingFallback />}>
              {currentView === 'upload' && (
                <FileUpload
                  onDataLoaded={handleDataImport}
                  activeClient={activeClient}
                  activePeriod={activePeriod}
                  existingPrData={currentData?.prData || []}
                  existingGstrData={currentData?.gstrData || []}
                />
              )}
              {currentView === 'data-editor' && currentData && (
                <DataEditor
                  prData={currentData.prData}
                  gstrData={currentData.gstrData}
                  onUpdate={handleUpdateRawData}
                  onSync={syncToDrive}
                  onLoadFromDrive={loadFromDrive}
                  syncStatus={syncStatus}
                  onReconcile={() => setCurrentView('reconciliation')}
                />
              )}
              {currentView === 'dashboard' && currentData && <Dashboard stats={currentData.stats} items={currentData.results} onFilter={handleDashboardFilter} />}
              {currentView === 'reconciliation' && currentData && (
                <ReconciliationTable
                  data={currentData.results}
                  prData={currentData.prData}
                  gstrData={currentData.gstrData}
                  onManualAction={(id, action) => bulkUpdateReconciliationItems([id], action)}
                  onBulkAction={bulkUpdateReconciliationItems}
                  onUpdateInvoice={(id, fields, type) => {
                    const updateList = (l: any[]) => l.map(i => i.id === id ? { ...i, ...fields } : i);
                    const pr = type === 'PR' ? updateList(currentData.prData) : currentData.prData;
                    const gstr = type === '2B' ? updateList(currentData.gstrData) : currentData.gstrData;
                    processDataUpdate(pr, gstr);
                  }}
                  onUpdateItem={updateReconciliationItem}
                  initialFilter={dashboardFilter}
                />
              )}
              {currentView === 'settings' && <Settings
                settings={settings}
                curUser={user!}
                onSave={(s) => { saveSettings(s); alert("Settings saved."); }}
                onLocalBackup={backupData}
                onLocalRestore={(path) => restoreData(path).then(success => success ? alert("Restored") : alert("Failed"))}
              />}
              {!currentData && currentView !== 'upload' && currentView !== 'settings' && (
                <div className="flex items-center justify-center h-full text-slate-300 italic font-medium">
                  Please Import Data to Begin
                </div>
              )}
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
};

export default App;